import { toast } from '@tamagui/toast/v2';
import { Stack, useLocalSearchParams, useRouter } from 'expo-router';
import Head from 'expo-router/head';
import { useState } from 'react';
import { getTokenValue, YStack } from 'tamagui';

import PSFetchError from '@/components/PSFetchError';
import PageScrollView from '@/components/ui/PageScrollView';
import PSButton from '@/components/ui/PSButton';
import PSSkeleton from '@/components/ui/PSSkeleton';
import PSText from '@/components/ui/PSText';
import { asApiError } from '@/src/api/apiError';
import membershipsApi, {
  type MembershipDetail,
} from '@/src/api/membershipsApi';
import {
  useFetchMembership,
  type FetchMembershipState,
} from '@/src/hooks/useFetchMembership';
import { restoreSession } from '@/src/hooks/useRestoredSession';
import { formatDateWithYear } from '@/src/utils/dateFormatter';
import PSIcon from '@/components/ui/PSIcon';
import PSCircleButton from '@/components/ui/PSCircleButton';

/**
 * ── /my-memberships/[id]/cancel — the confirm step ───────────────────────
 *
 * A sheet over the membership it's about, rather than an inline "are you sure",
 * because the honest answer to "what happens if I do this" is a paragraph. Same
 * shape as the delete-account flow's confirm step, minus the typing gate — this
 * is reversible by buying again, and nothing is destroyed.
 *
 * ── One DELETE, two very different paragraphs ───────────────────────────
 * `DELETE /me/memberships/{id}` is also how an abandoned checkout is cleared —
 * the server allows it on `pending`, and Razorpay cancels a mandate that was
 * never authorized. So this screen serves both buttons on the detail page, and
 * [[cancelCopy]] is what tells the two apart: cancelling a live pass is about
 * what you KEEP and until when; cancelling a pending one is about what you never
 * started, and carries the warning not to do it if the money has already left.
 *
 * `presentation: 'modal'` is declared on the ROOT stack (app/_layout.tsx), the
 * way `auth` and `delete-account` are: a screen can't lift itself.
 *
 * It REFETCHES the membership rather than taking the state through the route.
 * What this page turns on is the cycle boundary and the status, and either
 * encoded in a URL is a value that can be stale or forged by the time it's read.
 * ─────────────────────────────────────────────────────────────────────────
 */

/**
 * The day access actually stops.
 *
 * Read BEFORE the cancel, so it's `renewsAt` — a live subscription reports only
 * a renewal, and the day it would have charged is the day entitlement ends once
 * it won't. `expiresAt` is the fallback for anything already running out.
 */
const accessEndsOn = (membership: MembershipDetail) =>
  membership.renewsAt ?? membership.expiresAt;

/**
 * The words for whichever cancellation this is. Together in one place so the
 * body below is only layout, the same way the detail page keeps its states.
 *
 * The pending paragraph's last sentence is the one that matters: a `pending`
 * membership is ALSO what a paid purchase looks like while its webhook is late,
 * and the wire can't tell the two apart. Cancelling that one would call off a
 * mandate the member has already been charged against, so the screen says so
 * rather than letting a confident-looking button imply it's free.
 */
function cancelCopy(membership: MembershipDetail) {
  if (membership.status === 'pending')
    return {
      title: `Cancel this ${membership.plan.name} payment?`,
      body: "This membership never started, so there's nothing to lose — cancelling clears the checkout and you can buy again whenever you like.\n\nIf you've already been charged, don't worry: it'll come through on its own.",
      confirm: 'Cancel payment',
      confirmLoading: 'Cancelling',
      keep: 'Keep waiting',
    };

  const endsOn = accessEndsOn(membership);

  return {
    title: `Cancel your ${membership.plan.name}?`,
    body: endsOn
      ? `You keep every benefit till ${formatDateWithYear(endsOn)} — you've already paid for this cycle. After that it won't renew, and you won't be charged again.`
      : "You keep every benefit till the end of the cycle you've already paid for. After that it won't renew, and you won't be charged again.",
    confirm: 'Cancel membership',
    confirmLoading: 'Cancelling',
    keep: 'Keep my membership',
  };
}

function CancelBody({ state }: { state: FetchMembershipState }) {
  const router = useRouter();
  const [isCancelling, setIsCancelling] = useState(false);

  if (state.status === 'loading')
    return (
      <PSSkeleton>
        <YStack alignSelf="stretch" gap="$lg">
          <PSSkeleton.Bone style={{ width: '70%', height: 28 }} />
          <PSSkeleton.Bone style={{ width: '100%', height: 72 }} />
          <PSSkeleton.Bone style={{ width: '100%', height: 44 }} />
        </YStack>
      </PSSkeleton>
    );

  if (state.status === 'error')
    return <PSFetchError message={state.error.message} onRetry={state.retry} />;

  const membership = state.membership;
  const copy = cancelCopy(membership);

  const handleCancel = async () => {
    setIsCancelling(true);
    try {
      await membershipsApi.cancelMembership(membership.id);
    } catch (caught) {
      toast.error(asApiError(caught).message);
      setIsCancelling(false);
      return;
    }

    // The cancel landed, so what `/me` reports has changed — `cancelling` for a
    // live pass, nothing at all for a pending one — and profile and home read the
    // membership off the session. Home's pending-payment banner is the one that
    // would otherwise sit there advertising a purchase that's been called off.
    // Still: failing to refresh is not a failed cancel, so it isn't reported as
    // one. The next `/me` fixes it.
    await restoreSession().catch((error: unknown) => {
      console.warn('Cancelled, but the session is stale.', error);
    });

    // 204 carries no body, so there is nothing to hand back — the detail screen
    // re-reads the membership when it regains focus.
    router.back();
  };

  return (
    <YStack gap="$xxl">
      <YStack gap="$sm">
        <PSText.Title color="$textDark">{copy.title}</PSText.Title>
        <PSText.Body1 color="$textDarkMuted">{copy.body}</PSText.Body1>
      </YStack>

      <YStack gap="$md">
        <PSButton
          variant="destructive"
          title={copy.confirm}
          loading={isCancelling}
          loadingTitle={copy.confirmLoading}
          fullWidth
          onPress={handleCancel}
        />
        {/* A button of its own, not just the header's ✕ and the swipe: keeping
            the membership is the outcome we'd rather make easy to pick. */}
        <PSButton
          variant="outline"
          title={copy.keep}
          disabled={isCancelling}
          fullWidth
          onPress={() => router.back()}
        />
      </YStack>
    </YStack>
  );
}

export default function CancelMembershipScreen() {
  const { id } = useLocalSearchParams<{ id: string }>();
  const router = useRouter();
  const state = useFetchMembership(id);
  const closeButton = (
    <PSCircleButton onPress={() => router.back()}>
      <PSIcon name={'close'} />
    </PSCircleButton>
  );

  return (
    <>
      <Head>
        <title>Cancel membership · Putting Scene</title>
      </Head>
      <Stack.Screen
        options={{
          headerTransparent: false,
          headerTitle: () => (
            <PSText.H1 color="$textDark" textAlign="center">
              Cancel
            </PSText.H1>
          ),
          headerStyle: { backgroundColor: getTokenValue('$background') },
          // ✕, not ←: a sheet is dismissed, it isn't navigated back from. The
          // `presentation: 'modal'` that lifts it is declared on the root stack
          // (see app/_layout.tsx) — a screen can't set that on itself.
          headerLeft: () => closeButton,
          unstable_headerLeftItems: () => [
            {
              type: 'custom' as const,
              element: closeButton,
              hidesSharedBackground: true,
            },
          ],
        }}
      />
      <PageScrollView headerTransparent={false}>
        <CancelBody state={state} />
      </PageScrollView>
    </>
  );
}
